import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	AppWindow,
	Play,
	Loader2,
	Search,
	CheckCircle,
	XCircle,
	AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { testAppClassification } from "@/services/simulation";
import { getAdminOrganizations } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";

const commonApps = [
	{ name: "Visual Studio Code", process: "code" },
	{ name: "Google Chrome", process: "chrome" },
	{ name: "Slack", process: "slack" },
	{ name: "Microsoft Teams", process: "teams" },
	{ name: "Zoom", process: "zoom" },
	{ name: "Figma", process: "figma" },
	{ name: "Discord", process: "discord" },
	{ name: "Spotify", process: "spotify" },
	{ name: "Netflix", process: "netflix" },
	{ name: "Postman", process: "postman" },
	{ name: "Terminal", process: "terminal" },
	{ name: "DBeaver", process: "dbeaver" },
];

const classificationColors: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
	productive: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle },
	neutral: { bg: "bg-gray-100", text: "text-gray-700", icon: AlertCircle },
	unproductive: { bg: "bg-red-100", text: "text-red-700", icon: XCircle },
	pending_review: { bg: "bg-amber-100", text: "text-amber-700", icon: AlertCircle },
};

export function AppClassifier() {
	const { toast } = useToast();
	const [formData, setFormData] = useState({
		appName: "Visual Studio Code",
		processName: "code",
		organizationId: "",
	});

	// Fetch organizations for dropdown
	const { data: organizations } = useQuery({
		queryKey: ["organizations-list"],
		queryFn: () => getAdminOrganizations({ limit: 100 }),
	});

	const classificationMutation = useMutation({
		mutationFn: testAppClassification,
		onError: (error: any) => {
			toast({
				title: "Error",
				description: error.message || "Failed to get classification",
				variant: "destructive",
			});
		},
	});

	const handleSubmit = () => {
		if (!formData.organizationId) {
			toast({
				title: "Error",
				description: "Please select an organization",
				variant: "destructive",
			});
			return;
		}
		classificationMutation.mutate(formData);
	};

	const result = classificationMutation.data;
	const ClassificationIcon = result
		? classificationColors[result.classification]?.icon
		: null;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<AppWindow className="w-6 h-6" />
						App Classifier
					</h1>
					<p className="text-gray-500 mt-1">
						Test app classification lookup for an organization
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Input Form */}
				<Card>
					<CardHeader>
						<CardTitle>App Information</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Organization Selection */}
						<div>
							<Label>Organization</Label>
							<Select
								value={formData.organizationId}
								onValueChange={(value) =>
									setFormData({ ...formData, organizationId: value })
								}
							>
								<SelectTrigger className="mt-2">
									<SelectValue placeholder="Select an organization" />
								</SelectTrigger>
								<SelectContent>
									{organizations?.organizations?.map((org) => (
										<SelectItem key={org._id} value={org._id}>
											{org.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Quick App Selection */}
						<div>
							<Label>Common Apps</Label>
							<div className="flex flex-wrap gap-2 mt-2">
								{commonApps.slice(0, 8).map((app) => (
									<Button
										key={app.name}
										size="sm"
										variant={formData.appName === app.name ? "default" : "outline"}
										onClick={() =>
											setFormData({
												...formData,
												appName: app.name,
												processName: app.process,
											})
										}
									>
										{app.name}
									</Button>
								))}
							</div>
						</div>

						{/* App Name */}
						<div>
							<Label>App Name</Label>
							<div className="relative mt-2">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<Input
									className="pl-10"
									placeholder="Enter app name..."
									value={formData.appName}
									onChange={(e) =>
										setFormData({ ...formData, appName: e.target.value })
									}
								/>
							</div>
						</div>

						{/* Process Name */}
						<div>
							<Label>Process Name (optional)</Label>
							<Input
								className="mt-2"
								placeholder="e.g., chrome, code, slack"
								value={formData.processName}
								onChange={(e) =>
									setFormData({ ...formData, processName: e.target.value })
								}
							/>
							<p className="text-xs text-gray-500 mt-1">
								The executable name used for cross-platform matching
							</p>
						</div>

						{/* Lookup Button */}
						<Button
							className="w-full"
							size="lg"
							onClick={handleSubmit}
							disabled={classificationMutation.isPending || !formData.organizationId}
						>
							{classificationMutation.isPending ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<Play className="w-4 h-4 mr-2" />
							)}
							Lookup Classification
						</Button>
					</CardContent>
				</Card>

				{/* Results */}
				<Card>
					<CardHeader>
						<CardTitle>Classification Result</CardTitle>
					</CardHeader>
					<CardContent>
						{result ? (
							<div className="space-y-6">
								{/* Classification Status */}
								<div className={cn(
									"p-6 rounded-lg text-center",
									classificationColors[result.classification]?.bg
								)}>
									{ClassificationIcon && (
										<ClassificationIcon
											className={cn(
												"w-12 h-12 mx-auto mb-3",
												classificationColors[result.classification]?.text
											)}
										/>
									)}
									<p className={cn(
										"font-medium capitalize text-lg",
										classificationColors[result.classification]?.text
									)}>
										{result.classification.replace(/_/g, " ")}
									</p>
									{!result.found && (
										<p className="text-sm text-gray-600 mt-2">
											No custom classification found - using defaults
										</p>
									)}
								</div>

								{/* Details */}
								<div className="space-y-3">
									<div className="p-3 bg-gray-50 rounded-lg">
										<p className="text-xs text-gray-500 uppercase font-medium mb-1">
											App Name
										</p>
										<p className="font-medium">{result.appName}</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<p className="text-xs text-gray-500 uppercase font-medium mb-1">
											Base Score
										</p>
										<div className="flex items-center gap-2">
											<p className="text-2xl font-bold">{result.baseScore}</p>
											<div className="flex-1">
												<div className="w-full bg-gray-200 rounded-full h-2">
													<div
														className={cn(
															"h-2 rounded-full",
															result.baseScore >= 70
																? "bg-green-500"
																: result.baseScore >= 40
																	? "bg-amber-500"
																	: "bg-red-500"
														)}
														style={{ width: `${result.baseScore}%` }}
													/>
												</div>
											</div>
										</div>
									</div>
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
										<span className="text-sm text-gray-600">System Default</span>
										<Badge variant={result.isSystemDefault ? "default" : "outline"}>
											{result.isSystemDefault ? "Yes" : "No"}
										</Badge>
									</div>
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
										<span className="text-sm text-gray-600">Found in Database</span>
										<Badge variant={result.found ? "default" : "secondary"}>
											{result.found ? "Yes" : "No"}
										</Badge>
									</div>
								</div>

								{/* Context Rules */}
								{result.contextRules && result.contextRules.length > 0 && (
									<div>
										<h4 className="font-medium text-gray-700 mb-3">
											Context Rules
										</h4>
										<div className="space-y-2">
											{result.contextRules.map((rule, index) => (
												<div
													key={index}
													className="p-3 border rounded-lg text-sm"
												>
													<div className="flex items-center justify-between mb-1">
														<code className="bg-gray-100 px-2 py-0.5 rounded text-xs">
															{rule.pattern}
														</code>
														<Badge
															variant="outline"
															className={
																rule.scoreAdjust > 0
																	? "text-green-600"
																	: "text-red-600"
															}
														>
															{rule.scoreAdjust > 0 ? "+" : ""}
															{rule.scoreAdjust}
														</Badge>
													</div>
													<p className="text-gray-600">{rule.description}</p>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						) : (
							<div className="text-center py-12 text-gray-500">
								<AppWindow className="w-12 h-12 mx-auto mb-4 opacity-50" />
								<p>Select an organization and app to lookup classification</p>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
